import React, { useEffect, useState } from "react";

/*
  Themed onboarding "Setup Guide" popup (DialNepal blue + gold).
  - Auto-opens the first time (per storageKey), then only via the "Setup Guide" button.
  - Non-blocking: fully dismissible, never breaks the edit flow.

  props:
    storageKey : unique key so it shows once per user (e.g. "dn_guide_personal_<uid>")
    title      : heading
    intro      : short intro line
    steps      : [{ title, desc }]
*/
export default function SetupGuideModal({ storageKey, title, intro, steps = [] }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!storageKey) return;
        if (!localStorage.getItem(storageKey)) setOpen(true);
    }, [storageKey]);

    const close = () => {
        if (storageKey) localStorage.setItem(storageKey, "1");
        setOpen(false);
    };

    return (
        <>
            <button type="button" className="dn-guide-btn" onClick={() => setOpen(true)}>
                <i className="bi bi-info-circle me-1" /> Setup Guide
            </button>

            {open && (
                <div
                    className="dn-guide-overlay"
                    onMouseDown={(e) => e.target.classList.contains("dn-guide-overlay") && close()}
                >
                    <div className="dn-guide">
                        <div className="dn-guide-head">
                            <div>
                                <div className="dn-guide-eyebrow">Getting Started</div>
                                <h4 className="dn-guide-title">{title}</h4>
                            </div>
                            <button className="dn-guide-x" onClick={close} aria-label="Close">×</button>
                        </div>

                        <div className="dn-guide-body">
                            {intro && <p className="dn-guide-intro">{intro}</p>}
                            <ol className="dn-guide-steps">
                                {steps.map((s, i) => (
                                    <li key={i}>
                                        <span className="dn-guide-num">{i + 1}</span>
                                        <div>
                                            <div className="dn-guide-step-t">{s.title}</div>
                                            <div className="dn-guide-step-d">{s.desc}</div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                            <div className="dn-guide-tip">
                                <i className="bi bi-lightbulb-fill me-2" />
                                Fill each section and click <b>Save</b>. You can preview anytime with <b>View Live Website</b>.
                            </div>
                        </div>

                        <div className="dn-guide-foot">
                            <button className="dn-guide-start" onClick={close}>Got it, let's start</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .dn-guide-btn{
                    background:#eff4ff; color:#1e40af; border:1px solid #cfe0ff;
                    font-weight:600; font-size:13px; padding:6px 14px; border-radius:22px; cursor:pointer;
                    display:inline-flex; align-items:center; transition:.18s;
                }
                .dn-guide-btn:hover{ background:#2563eb; color:#fff; border-color:#2563eb; }

                .dn-guide-overlay{
                    position:fixed; inset:0; background:rgba(15,23,42,.55);
                    z-index:30000; display:flex; align-items:flex-start; justify-content:center;
                    padding:40px 14px; overflow-y:auto;
                }
                .dn-guide{
                    background:#fff; width:100%; max-width:520px; border-radius:16px; overflow:hidden;
                    box-shadow:0 24px 70px rgba(0,0,0,.35); font-family:'Inter',system-ui,Arial,sans-serif;
                    animation:dnGuideIn .22s ease;
                }
                @keyframes dnGuideIn{ from{ transform:translateY(12px); opacity:0; } to{ transform:none; opacity:1; } }

                .dn-guide-head{
                    display:flex; justify-content:space-between; align-items:flex-start;
                    padding:20px 22px; color:#fff;
                    background:linear-gradient(135deg,#1e40af,#2563eb);
                }
                .dn-guide-eyebrow{ font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#f6b100; font-weight:700; }
                .dn-guide-title{ font-family:'Poppins',sans-serif; font-weight:700; margin:2px 0 0; font-size:20px; }
                .dn-guide-x{ background:transparent; border:none; color:#fff; font-size:26px; line-height:1; cursor:pointer; opacity:.85; }
                .dn-guide-x:hover{ opacity:1; }

                .dn-guide-body{ padding:20px 22px; }
                .dn-guide-intro{ color:#475569; font-size:14px; margin:0 0 16px; }
                .dn-guide-steps{ list-style:none; padding:0; margin:0; }
                .dn-guide-steps li{ display:flex; gap:12px; padding:10px 0; border-bottom:1px dashed #eef1f6; }
                .dn-guide-steps li:last-child{ border-bottom:none; }
                .dn-guide-num{
                    flex:0 0 28px; width:28px; height:28px; border-radius:50%;
                    background:linear-gradient(135deg,#2563eb,#1e40af); color:#fff;
                    display:flex; align-items:center; justify-content:center; font-weight:700; font-size:13px;
                }
                .dn-guide-step-t{ font-weight:700; font-size:14px; color:#0f172a; }
                .dn-guide-step-d{ font-size:13px; color:#64748b; line-height:1.5; }

                .dn-guide-tip{
                    margin-top:16px; background:#fff8e6; border:1px solid #ffe6a3; color:#8a5a00;
                    padding:10px 12px; border-radius:10px; font-size:13px;
                }
                .dn-guide-tip b{ color:#6b4700; }

                .dn-guide-foot{ padding:0 22px 22px; }
                .dn-guide-start{
                    width:100%; border:none; border-radius:24px; padding:12px;
                    background:linear-gradient(135deg,#2563eb,#1e40af); color:#fff; font-weight:700; font-size:15px; cursor:pointer;
                    transition:.18s;
                }
                .dn-guide-start:hover{ filter:brightness(1.05); transform:translateY(-1px); }
            `}</style>
        </>
    );
}
